/**
 * Iter 98 — Agreements list screen (spec §13). One mobile-friendly list of
 * all rental agreements with signature status and one-tap PDF download.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Header from "@/components/Header";
import Money from "@/components/Money";
import OpsBottomNav from "@/components/ops/OpsBottomNav";
import BookingStatusBadge from "@/components/ops/BookingStatusBadge";
import { Input } from "@/components/ui/input";
import { FileDown, CheckCircle2, CircleDashed, Loader2 } from "lucide-react";

const PAGE = 20;

async function downloadPdf(bookingId) {
  try {
    const res = await fetch(`${API}/bookings/${bookingId}/agreement.pdf`, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rental-agreement-${bookingId.slice(0, 8).toUpperCase()}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  } catch {
    toast.error("Could not generate PDF. Please try again.");
  }
}

function SigChip({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold ${ok ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`}>
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <CircleDashed className="h-3 w-3" />} {label}
    </span>
  );
}

export default function AgreementsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const role = user?.role === "admin" || user?.role === "agent" ? "admin" : "vendor";

  const load = useCallback(async (skip = 0, query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ops/agreements?skip=${skip}&limit=${PAGE}${query ? `&q=${encodeURIComponent(query)}` : ""}`);
      setTotal(data.total);
      setItems((prev) => (skip === 0 ? data.items : [...prev, ...data.items]));
    } catch {
      toast.error("Could not load agreements");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(0, q.trim()), q ? 350 : 0);
    return () => clearTimeout(t);
  }, [q, load]);

  if (!user) return null;

  const handleBottomNav = (key) => {
    if (key === "agreements") return;
    if (key === "today-ops") { navigate("/dashboard/today"); return; }
    if (role === "admin") navigate(`/dashboard/admin#${key === "calendar" ? "bookings" : key === "dashboard" || key === "operations" ? "operations" : key}`);
    else navigate(key === "dashboard" ? "/dashboard/vendor" : `/dashboard/vendor?tab=${key}`);
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <div className="max-w-3xl mx-auto px-4 pt-6 pb-24" data-testid="agreements-page">
        <h1 className="font-display text-3xl tracking-tight font-medium mb-1">Agreements</h1>
        <div className="text-sm text-neutral-500 mb-4">{total} rental agreements</div>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by number, customer, vehicle, plate…"
          className="rounded-xl h-11 mb-4"
          data-testid="agreements-search"
        />
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="rounded-3xl border border-border bg-white p-4" data-testid={`agreement-card-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <Link to={`/agreements/${a.id}`} className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[11px] font-bold text-neutral-400">AG-{a.agreement_no}</span>
                    <BookingStatusBadge booking={a} />
                  </div>
                  <div className="font-bold mt-1 truncate">{a.car?.make} {a.car?.model}{a.car?.license_plate ? ` · ${a.car.license_plate}` : ""}</div>
                  <div className="text-sm text-neutral-600 truncate">{a.customer_name || "—"}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {String(a.start_date || "").slice(0, 10)} → {String(a.end_date || "").slice(0, 10)} · <Money amount={a.amount || 0} />
                  </div>
                </Link>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <SigChip ok={a.signatures?.customer} label="Customer signed" />
                <SigChip ok={a.signatures?.vendor} label="Provider signed" />
                {a.signatures?.return_done && <SigChip ok label="Return done" />}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-border">
                <Link
                  to={`/agreements/${a.id}`}
                  className="inline-flex items-center justify-center rounded-xl bg-neutral-900 text-white text-sm font-bold py-3 sm:py-2.5"
                  data-testid={`agreement-open-${a.id}`}
                >
                  Open agreement
                </Link>
                <button
                  type="button"
                  onClick={async () => { setDownloading(a.id); await downloadPdf(a.id); setDownloading(null); }}
                  disabled={downloading === a.id}
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border bg-white text-sm font-bold py-3 sm:py-2.5 disabled:opacity-60"
                  data-testid={`agreement-pdf-${a.id}`}
                >
                  {downloading === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF
                </button>
              </div>
            </div>
          ))}
        </div>
        {loading && <div className="text-center text-neutral-400 py-6">Loading…</div>}
        {!loading && items.length < total && !q && (
          <button
            type="button"
            onClick={() => load(items.length)}
            className="w-full mt-4 rounded-xl border border-border bg-white text-sm font-bold py-3.5"
            data-testid="agreements-load-more"
          >
            Load more ({items.length} of {total})
          </button>
        )}
        {!loading && items.length === 0 && (
          <div className="rounded-3xl border border-dashed border-border bg-white p-10 text-center text-neutral-500" data-testid="agreements-empty">
            No agreements found.
          </div>
        )}
      </div>
      <OpsBottomNav role={role} active="agreements" onNavigate={handleBottomNav} />
      <div className="h-16 md:hidden" />
    </div>
  );
}
